import type { CpuDifficulty } from 'features/battle/ai-config';

export type ClassicDifficultyRecord = { all: number; claimed: number };
export type ArenaDifficultyRecord = { runs: number; wins: number };

export type BattleWinEvent = {
  profileId: string;
  name: string;
  gameId: string;
  kind: 'classic_win' | 'arena_win';
  difficulty: CpuDifficulty;
  claimedOnly?: boolean;
  runComplete?: boolean;
};

export type BattleLeaderboardProfile = {
  profileId: string;
  name: string;
  updatedAt: number;
  classic: Record<CpuDifficulty, ClassicDifficultyRecord>;
  arena: Record<CpuDifficulty, ArenaDifficultyRecord>;
  recentGameIds: string[];
};

export type BattleBoardQuery =
  { mode: 'classic'; difficulty: CpuDifficulty; claimedOnly: boolean } | { mode: 'arena'; difficulty: CpuDifficulty };

export type BattleBoardRow = {
  profileId: string;
  name: string;
  count: number;
  secondary?: number;
};

export const emptyClassicRecord = (): ClassicDifficultyRecord => ({ all: 0, claimed: 0 });
export const emptyArenaRecord = (): ArenaDifficultyRecord => ({ runs: 0, wins: 0 });

export const emptyLeaderboardProfile = (profileId: string, name: string): BattleLeaderboardProfile => ({
  profileId,
  name,
  updatedAt: 0,
  classic: {
    easy: emptyClassicRecord(),
    hard: emptyClassicRecord(),
    impossible: emptyClassicRecord(),
  },
  arena: {
    easy: emptyArenaRecord(),
    hard: emptyArenaRecord(),
    impossible: emptyArenaRecord(),
  },
  recentGameIds: [],
});

const cloneCounts = (profile: BattleLeaderboardProfile): BattleLeaderboardProfile => ({
  ...profile,
  classic: {
    easy: { ...profile.classic.easy },
    hard: { ...profile.classic.hard },
    impossible: { ...profile.classic.impossible },
  },
  arena: {
    easy: { ...profile.arena.easy },
    hard: { ...profile.arena.hard },
    impossible: { ...profile.arena.impossible },
  },
  recentGameIds: [...profile.recentGameIds],
});

export const applyBattleWin = (profile: BattleLeaderboardProfile, event: BattleWinEvent): BattleLeaderboardProfile => {
  if (profile.recentGameIds.includes(event.gameId)) return profile;
  const next = cloneCounts(profile);
  next.name = event.name || profile.name;
  next.updatedAt = Date.now();
  next.recentGameIds = [...next.recentGameIds, event.gameId].slice(-30);
  if (event.kind === 'classic_win') {
    const record = next.classic[event.difficulty];
    record.all += 1;
    if (event.claimedOnly) record.claimed += 1;
  } else if (event.runComplete) {
    const record = next.arena[event.difficulty];
    record.runs += 1;
    record.wins += 1;
  }
  return next;
};

export const mergePendingBattleWins = (
  profiles: readonly BattleLeaderboardProfile[],
  events: readonly BattleWinEvent[]
): BattleLeaderboardProfile[] => {
  const byId = new Map(profiles.map((profile) => [profile.profileId, profile]));
  for (const event of events) {
    const existing = byId.get(event.profileId) ?? emptyLeaderboardProfile(event.profileId, event.name);
    byId.set(event.profileId, applyBattleWin(existing, event));
  }
  return [...byId.values()];
};

export const rankBattleBoard = (
  profiles: readonly BattleLeaderboardProfile[],
  board: BattleBoardQuery
): BattleBoardRow[] => {
  const rows: BattleBoardRow[] = profiles.map((profile) => {
    if (board.mode === 'classic') {
      const record = profile.classic[board.difficulty];
      return {
        profileId: profile.profileId,
        name: profile.name,
        count: board.claimedOnly ? record.claimed : record.all,
      };
    }
    const record = profile.arena[board.difficulty];
    return {
      profileId: profile.profileId,
      name: profile.name,
      count: record.runs,
    };
  });
  return rows
    .filter((row) => row.count > 0)
    .sort((a, b) => b.count - a.count || (b.secondary ?? 0) - (a.secondary ?? 0) || a.name.localeCompare(b.name));
};
